import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { HiOutlineArrowDown, HiOutlineArrowUp, HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useFieldConfigError from '@/hooks/useFieldConfigError.ts';
import { ConfigFieldValue } from '@/types/automationFields.ts';
import { IntegrationTextConfigField } from '@/types/integrations.ts';
import {
  BrowserMultiStageFlowDefinition,
  BrowserFlowStageDefinition,
  BrowserStageReadinessCondition,
  BrowserStageOutcomeField,
  createDefaultBrowserActionStages,
  withStateObservation,
  createDefaultBrowserExtractionStage,
  isBrowserCaptureSpec,
  type BrowserCaptureSpec,
  createDefaultBrowserInteractionStage,
  createDefaultBrowserInteractionStages,
  createDefaultBrowserFlowStage,
  createNextDefaultBrowserFlowStage,
  isDeterministicBrowserPlan,
  parseBrowserFlowAuthoringSpec,
  parseBrowserMultiStageFlowDefinition,
  serializeBrowserMultiStageFlowDefinition,
} from '@/utils/browserFlow.ts';

type Props = {
  stepId: string;
  field: IntegrationTextConfigField;
  fieldValue: unknown;
  fieldValueConfig: ConfigFieldValue;
  onChange: (updatedValue: string) => void;
};

const makeDefaultDefinition = (): BrowserMultiStageFlowDefinition => ({
  schema_version: 'browser_multi_stage_flow.v2',
  stages: [createDefaultBrowserFlowStage(0)],
});

const BrowserMultiStageFlowField = ({ stepId, field, fieldValue, fieldValueConfig, onChange }: Props) => {
  const error = useFieldConfigError(stepId, fieldValueConfig);
  const isEmptyValue = fieldValue == null || (typeof fieldValue === 'string' && fieldValue.trim() === '');
  const parsed = useMemo(() => parseBrowserMultiStageFlowDefinition(fieldValue), [fieldValue]);
  const authoringSpec = useMemo(() => parseBrowserFlowAuthoringSpec(field.authoring_schema), [field.authoring_schema]);
  const [rawValue, setRawValue] = useState(typeof fieldValue === 'string' ? fieldValue : '');
  const [executorConfigDrafts, setExecutorConfigDrafts] = useState<Record<string, string>>({});
  const [executorConfigDraftErrors, setExecutorConfigDraftErrors] = useState<Record<string, string>>({});
  const [numericDrafts, setNumericDrafts] = useState<Record<string, string>>({});
  const [draftDefinition, setDraftDefinition] = useState<BrowserMultiStageFlowDefinition | null>(null);

  useEffect(() => {
    setRawValue(typeof fieldValue === 'string' ? fieldValue : '');
  }, [fieldValue]);

  useEffect(() => {
    if (Object.keys(executorConfigDraftErrors).length === 0) {
      setDraftDefinition(null);
    }
  }, [executorConfigDraftErrors, fieldValue]);

  const definition = draftDefinition ?? (isEmptyValue ? makeDefaultDefinition() : parsed.value ?? makeDefaultDefinition());

  const applyDefinition = (nextDefinition: BrowserMultiStageFlowDefinition) => {
    setDraftDefinition(nextDefinition);
    onChange(serializeBrowserMultiStageFlowDefinition(nextDefinition));
  };

  const numericDraftKey = (stageIndex: number, fieldIndex: number, field: string) =>
    `${stageIndex}:${fieldIndex}:${field}`;

  const parseOptionalInteger = (value: string): number | undefined | null => {
    if (value === '') {
      return undefined;
    }
    if (!/^-?\d+$/.test(value)) {
      return null;
    }
    const parsedValue = Number(value);
    return Number.isSafeInteger(parsedValue) ? parsedValue : null;
  };

  const numericDraftError = (key: string) =>
    numericDrafts[key] !== undefined && parseOptionalInteger(numericDrafts[key]) === null
      ? 'Enter a whole number.'
      : undefined;

  const updateOutcomeIntegerBound = (
    stageIndex: number,
    fieldIndex: number,
    bound: 'minimum' | 'maximum',
    value: string,
  ) => {
    const key = numericDraftKey(stageIndex, fieldIndex, bound);
    setNumericDrafts((drafts) => ({ ...drafts, [key]: value }));
    const parsedValue = parseOptionalInteger(value);
    if (parsedValue === null) {
      return;
    }
    updateOutcomeField(stageIndex, fieldIndex, (current) => ({ ...current, [bound]: parsedValue }));
  };

  const readinessDraftKey = (stageIndex: number, conditionIndex: number) =>
    numericDraftKey(stageIndex, conditionIndex, 'readiness_allowed_values');

  const parseIntegerList = (value: string): number[] | null => {
    const values = value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (values.length === 0) {
      return null;
    }
    const parsedValues = values.map(parseOptionalInteger);
    return parsedValues.every((entry): entry is number => typeof entry === 'number') ? parsedValues : null;
  };

  const updateStage = (stageIndex: number, updater: (stage: BrowserFlowStageDefinition) => BrowserFlowStageDefinition) => {
    const currentDefinition = definition;
    applyDefinition({
      ...currentDefinition,
      stages: currentDefinition.stages.map((stage, index) =>
        index === stageIndex ? updater(stage) : stage,
      ),
    });
  };

  const updateOutcomeField = (
    stageIndex: number,
    fieldIndex: number,
    updater: (outcomeField: BrowserStageOutcomeField) => BrowserStageOutcomeField,
  ) => {
    const currentDefinition = definition;
    const stage = currentDefinition.stages[stageIndex];
    if (!stage) {
      return;
    }
    applyDefinition({
      ...currentDefinition,
      stages: currentDefinition.stages.map((candidate, candidateIndex) =>
        candidateIndex !== stageIndex
          ? candidate
          : {
              ...candidate,
              outcome_contract: {
                ...candidate.outcome_contract,
                fields: candidate.outcome_contract.fields.map((outcomeField, index) =>
                  index === fieldIndex ? updater(outcomeField) : outcomeField,
                ),
              },
            },
      ),
    });
  };

  const updateReadinessPolicy = (
    stageIndex: number,
    updater: (
      policy: BrowserFlowStageDefinition['readiness_policy'],
      stage: BrowserFlowStageDefinition,
    ) => BrowserFlowStageDefinition['readiness_policy'],
  ) => {
    updateStage(stageIndex, (stage) => ({
      ...stage,
      readiness_policy: updater(stage.readiness_policy, stage),
    }));
  };

  const readinessEligibleFields = (stage: BrowserFlowStageDefinition) =>
    stage.outcome_contract.fields.filter(
      (outcomeField) =>
        outcomeField.required !== false &&
        (outcomeField.value_type === 'boolean' ||
          outcomeField.value_type === 'integer' ||
          outcomeField.value_type === 'enum'),
    );

  const defaultReadinessAllowedValues = (outcomeField: BrowserStageOutcomeField): BrowserStageReadinessCondition['allowed_values'] => {
    if (outcomeField.value_type === 'boolean') {
      return [true];
    }
    if (outcomeField.value_type === 'integer') {
      return [outcomeField.minimum ?? 0];
    }
    return [outcomeField.enum_values?.[0] ?? 'ready'];
  };

  const isReadinessReferenced = (stage: BrowserFlowStageDefinition, outcomeField: BrowserStageOutcomeField) =>
    stage.readiness_policy?.conditions.some((condition) => condition.field_name === outcomeField.name) ?? false;

  const addOutcomeField = (stageIndex: number) => {
    const currentDefinition = definition;
    applyDefinition({
      ...currentDefinition,
      stages: currentDefinition.stages.map((stage, index) =>
        index !== stageIndex
          ? stage
          : {
              ...stage,
              outcome_contract: {
                ...stage.outcome_contract,
                fields: [
                  ...stage.outcome_contract.fields,
                  { name: `outcome_${stage.outcome_contract.fields.length + 1}`, value_type: 'boolean', required: true },
                ],
              },
            },
      ),
    });
  };

  const removeOutcomeField = (stageIndex: number, fieldIndex: number) => {
    const currentDefinition = definition;
    applyDefinition({
      ...currentDefinition,
      stages: currentDefinition.stages.map((stage, index) =>
        index !== stageIndex
          ? stage
          : {
              ...stage,
              outcome_contract: {
                ...stage.outcome_contract,
                fields: stage.outcome_contract.fields.filter((_, index) => index !== fieldIndex),
              },
            },
      ),
    });
  };

  const addStage = (kind: 'narrative' | 'verification' | 'deterministic_interaction' | 'structured_extraction' | 'action') => {
    const currentDefinition = definition;
    if (kind === 'structured_extraction') {
      const stage = createDefaultBrowserExtractionStage(currentDefinition.stages);
      applyDefinition({ ...currentDefinition, stages: [...currentDefinition.stages, stage] });
      return;
    }
    if (kind === 'action') {
      const [action, observer] = createDefaultBrowserActionStages(currentDefinition.stages);
      applyDefinition({ ...currentDefinition, stages: [...currentDefinition.stages, action, observer] });
      return;
    }
    if (kind === 'deterministic_interaction') {
      const actionIndex = currentDefinition.stages.findIndex((stage) => stage.stage_kind === 'action' || stage.stage_kind === 'structured_extraction');
      if (actionIndex >= 0) {
        const interaction = createDefaultBrowserInteractionStage(currentDefinition.stages);
        applyDefinition({
          ...currentDefinition,
          stages: [
            ...currentDefinition.stages.slice(0, actionIndex),
            interaction,
            ...currentDefinition.stages.slice(actionIndex),
          ],
        });
        return;
      }
      const [interaction, followUp] = createDefaultBrowserInteractionStages(currentDefinition.stages);
      applyDefinition({ ...currentDefinition, stages: [...currentDefinition.stages, interaction, followUp] });
      return;
    }
    const stage = createNextDefaultBrowserFlowStage(currentDefinition.stages);
    const capture = currentDefinition.stages.at(-1)?.stage_kind === 'structured_extraction'
      ? currentDefinition.stages.slice(-1) : [];
    applyDefinition({
      ...currentDefinition,
      stages: [
        ...currentDefinition.stages.slice(0, currentDefinition.stages.length - capture.length),
        kind === 'verification'
          ? {
              ...stage,
              stage_kind: 'verification',
              outcome_contract: {
                schema_version: 'browser_stage_outcome_contract.v2',
                fields: [{ name: 'ready', value_type: 'boolean', required: true }],
              },
            }
          : stage,
        ...capture,
      ],
    });
  };

  const stageUnits = (stages: BrowserFlowStageDefinition[]) => {
    const units: Array<{ start: number; end: number }> = [];
    for (let index = 0; index < stages.length; index += 1) {
      if (stages[index]?.stage_kind === 'post_action_observation') {
        units.push({ start: index, end: index });
        continue;
      }
      const isActionPair =
        stages[index]?.stage_kind === 'action' && stages[index + 1]?.stage_kind === 'post_action_observation';
      units.push({ start: index, end: isActionPair ? index + 1 : index });
      if (isActionPair) {
        index += 1;
      }
    }
    return units;
  };

  const definitionAfterRemoval = (stageIndex: number) => {
    const unit = stageUnits(definition.stages).find((candidate) => candidate.start === stageIndex);
    if (!unit) return null;
    const nextStages = definition.stages.filter((_, index) => index < unit.start || index > unit.end);
    const candidate = {
      ...definition,
      stages: nextStages.length > 0 ? nextStages : [createDefaultBrowserFlowStage(0)],
    };
    return parseBrowserMultiStageFlowDefinition(candidate).value ? { unit, candidate } : null;
  };

  const canRemoveStage = (stageIndex: number) => !!definitionAfterRemoval(stageIndex);

  const removeStage = (stageIndex: number) => {
    const removal = definitionAfterRemoval(stageIndex);
    if (!removal) return;
    const { unit, candidate } = removal;
    const removedIds = new Set(definition.stages.slice(unit.start, unit.end + 1).map((stage) => stage.stage_id));
    const withoutRemovedStages = (drafts: Record<string, string>) =>
      Object.fromEntries(Object.entries(drafts).filter(([id]) => !removedIds.has(id)));
    setExecutorConfigDrafts(withoutRemovedStages);
    setExecutorConfigDraftErrors(withoutRemovedStages);
    applyDefinition(candidate);
  };

  const definitionAfterMove = (stageIndex: number, direction: -1 | 1) => {
    const units = stageUnits(definition.stages);
    const unitIndex = units.findIndex((unit) => unit.start === stageIndex);
    const target = units[unitIndex + direction];
    if (
      unitIndex < 0 || !target ||
      definition.stages[stageIndex]?.stage_kind === 'structured_extraction' ||
      definition.stages[target.start]?.stage_kind === 'structured_extraction'
    ) {
      return null;
    }
    const targetUnitIndex = unitIndex + direction;
    const orderedUnits = [...units];
    [orderedUnits[unitIndex], orderedUnits[targetUnitIndex]] = [orderedUnits[targetUnitIndex], orderedUnits[unitIndex]];
    const candidate = {
      ...definition,
      stages: orderedUnits.flatMap((unit) => definition.stages.slice(unit.start, unit.end + 1)),
    };
    return parseBrowserMultiStageFlowDefinition(candidate).value ? candidate : null;
  };

  const canMoveStage = (stageIndex: number, direction: -1 | 1) => !!definitionAfterMove(stageIndex, direction);

  const moveStage = (stageIndex: number, direction: -1 | 1) => {
    const candidate = definitionAfterMove(stageIndex, direction);
    if (candidate) applyDefinition(candidate);
  };

  const executorConfigDraftFor = (stage: BrowserFlowStageDefinition) =>
    executorConfigDrafts[stage.stage_id] ??
    JSON.stringify(
      stage.stage_kind === 'structured_extraction'
        ? (stage.extraction ?? {})
        : (stage.executor_config ?? {}),
      null,
      2,
    );

  const changeVerificationMethod = (stageIndex: number, executor: string | null) => {
    const stage = definition.stages[stageIndex];
    if (!stage || !executor || executor === (stage.executor ?? 'browser_use_agent')) return;
    const withoutStage = (drafts: Record<string, string>) =>
      Object.fromEntries(Object.entries(drafts).filter(([id]) => id !== stage.stage_id));
    setExecutorConfigDrafts(withoutStage);
    setExecutorConfigDraftErrors(withoutStage);
    updateStage(stageIndex, current => executor === 'deterministic_cdp' ? withStateObservation(current) : ({
      ...current,
      executor: 'browser_use_agent',
      executor_config: {},
      result_completion: null,
      outcome_contract: {
        schema_version: 'browser_stage_outcome_contract.v2',
        fields: [{ name: 'ready', value_type: 'boolean', required: true }],
      },
      readiness_policy: {
        schema_version: 'browser_stage_readiness_policy.v1',
        conditions: [{ field_name: 'ready', allowed_values: [true] }],
      },
    }));
  };

  const updateDeterministicExecutorConfig = (stageIndex: number, value: string) => {
    const stage = definition.stages[stageIndex];
    if (!stage) {
      return;
    }
    setExecutorConfigDrafts((drafts) => ({ ...drafts, [stage.stage_id]: value }));
    try {
      const parsedConfig = JSON.parse(value) as unknown;
      if (stage.stage_kind === 'structured_extraction') {
        if (!isBrowserCaptureSpec(parsedConfig)) throw new Error('Invalid capture');
        setExecutorConfigDraftErrors((errors) => {
          const { [stage.stage_id]: _, ...rest } = errors;
          return rest;
        });
        updateStage(stageIndex, (current) => ({
          ...current,
          extraction: parsedConfig as BrowserCaptureSpec,
        }));
        return;
      }
      if (!isDeterministicBrowserPlan(parsedConfig) ||
          (stage.stage_kind === 'verification' && parsedConfig.control_plan_id !== 'state_observation')) {
        setExecutorConfigDraftErrors((errors) => ({
          ...errors,
          [stage.stage_id]: 'The deterministic CDP plan must match deterministic_browser_plan.v1.',
        }));
        updateStage(stageIndex, (currentStage) => ({ ...currentStage, executor_config: {} }));
        return;
      }
      setExecutorConfigDraftErrors((errors) => {
        const { [stage.stage_id]: _, ...remainingErrors } = errors;
        return remainingErrors;
      });
      updateStage(stageIndex, (currentStage) => ({
        ...currentStage,
        executor_config: parsedConfig as Record<string, unknown>,
      }));
    } catch {
      setExecutorConfigDraftErrors((errors) => ({
        ...errors,
        [stage.stage_id]:
          stage.stage_kind === 'structured_extraction'
            ? 'Enter a valid bounded capture specification before saving.'
            : 'The deterministic CDP plan must contain valid JSON before it can be saved.',
      }));
      updateStage(stageIndex, (currentStage) =>
        stage.stage_kind === 'structured_extraction'
          ? { ...currentStage, extraction: null }
          : { ...currentStage, executor_config: {} },
      );
    }
  };

  if (!isEmptyValue && parsed.error && draftDefinition === null) {
    return (
      <Box pos="relative">
        <Stack gap="sm">
          <Alert color="red" title={field.display_name}>
            {parsed.error} Use the recovery editor below to fix or replace the stored value.
          </Alert>
          <StyledTextArea
            label="Recovery JSON"
            value={rawValue}
            description={field.description ?? undefined}
            placeholder={field.placeholder ?? undefined}
            onChange={(event) => {
              const nextValue = event.currentTarget.value;
              setRawValue(nextValue);
              onChange(nextValue);
            }}
            required={field.required}
            autosize
            minRows={8}
            maxLength={field.max_length ?? undefined}
            error={error?.message}
            disabled={fieldValueConfig.is_disabled}
          />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              This fallback is only for recovering malformed existing values.
            </Text>
            <Button
              variant="light"
              onClick={() => {
                const nextDefinition = makeDefaultDefinition();
                const nextValue = serializeBrowserMultiStageFlowDefinition(nextDefinition);
                setRawValue(nextValue);
                onChange(nextValue);
              }}
              disabled={fieldValueConfig.is_disabled}
            >
              Reset to Structured Template
            </Button>
          </Group>
        </Stack>
      </Box>
    );
  }

  if (!authoringSpec) {
    return (
      <Alert color="yellow" title={field.display_name}>
        Browser Flow authoring metadata is unavailable or incompatible. The field is read-only until the backend and
        Automation Editor are on the same Browser Flow V2 authoring-spec version.
      </Alert>
    );
  }

  return (
    <Box pos="relative">
      <Stack gap="sm">
        <Stack gap={4}>
          <Group justify="space-between" align="flex-start" gap="sm">
            <div>
              <Text size="sm" fw={500}>
                {field.display_name}
              </Text>
              {field.description && (
                <Text size="sm" c="dimmed">
                  {field.description}
                </Text>
              )}
            </div>
            <Badge variant="light" color="blue">
              {definition.stages.length} stage{definition.stages.length === 1 ? '' : 's'}
            </Badge>
          </Group>
          {error?.message && (
            <Text size="sm" c="red">
              {error.message}
            </Text>
          )}
        </Stack>

        {definition.stages.map((stage, stageIndex) => {
          if (stage.stage_kind === 'post_action_observation') {
            return null;
          }
          const observer =
            stage.stage_kind === 'action' && definition.stages[stageIndex + 1]?.stage_kind === 'post_action_observation'
              ? definition.stages[stageIndex + 1]
              : null;
          const platformObserverFields =
            (authoringSpec.stage_kinds.post_action_observation?.platform_owned_outcome_fields as string[] | undefined) ??
            [];
          return (
          <Box
            key={stageIndex}
            px="sm"
            py="sm"
            bg="white"
            style={{ border: '1px solid #ECECED', borderRadius: '8px' }}
          >
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <Badge variant="light" color="gray">
                    Stage {stageIndex + 1}
                  </Badge>
                  <Text size="sm" fw={600}>
                    {stage.label || `Stage ${stageIndex + 1}`}
                  </Text>
                </Group>
                <Group gap={4}>
                  <ActionIcon
                    aria-label={`Move stage ${stageIndex + 1} up`}
                    variant="subtle"
                    color="gray"
                    disabled={fieldValueConfig.is_disabled || !canMoveStage(stageIndex, -1)}
                    onClick={() => moveStage(stageIndex, -1)}
                  >
                    <HiOutlineArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    aria-label={`Move stage ${stageIndex + 1} down`}
                    variant="subtle"
                    color="gray"
                    disabled={fieldValueConfig.is_disabled || !canMoveStage(stageIndex, 1)}
                    onClick={() => moveStage(stageIndex, 1)}
                  >
                    <HiOutlineArrowDown size={16} />
                  </ActionIcon>
                  <ActionIcon
                    aria-label={`Remove stage ${stageIndex + 1}`}
                    variant="subtle"
                    color="red"
                    disabled={fieldValueConfig.is_disabled || !canRemoveStage(stageIndex)}
                    onClick={() => removeStage(stageIndex)}
                  >
                    <HiOutlineTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>

              <StyledTextInput
                label="Stage ID"
                value={stage.stage_id}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    stage_id: event.currentTarget.value,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
                required
              />
              <StyledTextInput
                label="Label"
                value={stage.label}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    label: event.currentTarget.value,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
                required
              />
              <StyledTextArea
                label="Instructions"
                value={stage.instruction_text}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    instruction_text: event.currentTarget.value,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
                minRows={3}
                autosize
                required
              />
              {stage.stage_kind === 'verification' && (
                <Select
                  label="Verification method"
                  description="Changing the method replaces this stage's checks and readiness contract."
                  value={stage.executor ?? 'browser_use_agent'}
                  data={[{ value: 'browser_use_agent', label: 'Browser agent' }, { value: 'deterministic_cdp', label: 'Page state checks' }]}
                  disabled={fieldValueConfig.is_disabled}
                  onChange={value => changeVerificationMethod(stageIndex, value)}
                />
              )}
              {stage.stage_kind === 'structured_extraction' ? (
                  <StyledTextArea
                    label="Structured Capture JSON"
                    description="Read-only extraction from the current page. Define the allowed origins, selectors, fields and version-bound schema. The backend validates these settings when saved."
                    value={executorConfigDraftFor(stage)}
                    onChange={(event) =>
                      updateDeterministicExecutorConfig(stageIndex, event.currentTarget.value)
                    }
                    error={executorConfigDraftErrors[stage.stage_id]}
                    disabled={fieldValueConfig.is_disabled}
                    minRows={12}
                    autosize
                    required
                  />
                ) : stage.stage_kind === 'verification' && stage.executor === 'deterministic_cdp' ? (
                  <StyledTextArea
                    label="Page State Checks JSON"
                    description="Wait for all reviewed checks in one current page snapshot. Uses Expected page state data for context pointers. This stage cannot click, reload or retry an action."
                    value={executorConfigDraftFor(stage)}
                    onChange={event => updateDeterministicExecutorConfig(stageIndex, event.currentTarget.value)}
                    error={executorConfigDraftErrors[stage.stage_id]}
                    disabled={fieldValueConfig.is_disabled}
                    minRows={12} autosize required
                  />
                ) : stage.stage_kind === 'action' || stage.stage_kind === 'deterministic_interaction' ? (
                <>
                  <Alert
                    color="blue"
                    title={
                      stage.stage_kind === 'action'
                        ? 'Deterministic action with required post-action observation'
                        : 'Deterministic pre-submit interaction'
                    }
                  >
                    {stage.stage_kind === 'action'
                      ? 'This action is structurally paired with the final observation stage. The pair moves and deletes together. Browser agents cannot perform this action.'
                      : 'This stage uses the carried Browser Use session for a reviewed, bounded pre-submit interaction. It must be followed by later flow work and cannot represent the final irreversible action.'}
                  </Alert>
                  <StyledTextArea
                    label="Deterministic CDP Plan JSON"
                    description="Use an explicit page_match for actions and an exact role/name or unique selector for the final click. Every mutation must resolve to one compatible control."
                    value={executorConfigDraftFor(stage)}
                    onChange={(event) => updateDeterministicExecutorConfig(stageIndex, event.currentTarget.value)}
                    disabled={fieldValueConfig.is_disabled}
                    minRows={8}
                    autosize
                    required
                  />
                  {executorConfigDraftErrors[stage.stage_id] && (
                    <Text size="sm" c="red">
                      {executorConfigDraftErrors[stage.stage_id]}
                    </Text>
                  )}
                </>
              ) : (
                <Select
                  label="Stage Kind"
                  value={stage.stage_kind}
                  data={[
                    { value: 'narrative', label: 'Narrative' },
                    { value: 'verification', label: 'Verification' },
                  ]}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    updateStage(stageIndex, (currentStage) => ({
                      ...currentStage,
                      stage_kind: value as 'narrative' | 'verification',
                      outcome_contract:
                        value === 'verification'
                          ? {
                              schema_version: 'browser_stage_outcome_contract.v2',
                              fields:
                                currentStage.outcome_contract.fields.length > 0
                                  ? currentStage.outcome_contract.fields
                                  : [{ name: 'ready', value_type: 'boolean', required: true }],
                            }
                          : { schema_version: 'browser_stage_outcome_contract.v2', fields: [] },
                      readiness_policy: value === 'narrative' ? null : currentStage.readiness_policy,
                      result_completion: value === 'narrative' ? null : currentStage.result_completion,
                    }));
                  }}
                  disabled={fieldValueConfig.is_disabled}
                />
              )}
              {stage.stage_kind === 'structured_extraction' ? (
                <Text size="sm" c="dimmed">Connect the Browser output directly to Code. This final stage returns exact JSON with a byte count and checksum; ambiguous selectors or exceeded limits fail the stage.</Text>
              ) : stage.stage_kind === 'verification' && stage.executor === 'deterministic_cdp' ? (
                <Text size="sm" c="dimmed">The fixed readiness gate requires state_status=ready. Blocked, not ready and unknown stop the flow.</Text>
              ) : stage.stage_kind === 'narrative' ? (
                <Text size="sm" c="dimmed">
                  Narrative stages intentionally do not return structured outcome facts.
                </Text>
              ) : (
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Outcome Fields
                  </Text>
                  {stage.outcome_contract.fields.map((outcomeField, fieldIndex) => {
                    const readinessReferenced = isReadinessReferenced(stage, outcomeField);
                    return (
                    <Box key={`${stageIndex}-${fieldIndex}`} p="xs" bg="gray.0" style={{ borderRadius: '6px' }}>
                      <Stack gap="xs">
                        <Group grow align="end">
                          <StyledTextInput
                            label="Name"
                            value={outcomeField.name}
                            onChange={(event) =>
                              updateOutcomeField(stageIndex, fieldIndex, (current) => ({
                                ...current,
                                name: event.currentTarget.value,
                              }))
                            }
                            disabled={fieldValueConfig.is_disabled || readinessReferenced}
                          />
                          <Select
                            label="Type"
                            value={outcomeField.value_type}
                            data={[
                              { value: 'boolean', label: 'Boolean' },
                              { value: 'integer', label: 'Integer' },
                              { value: 'string', label: 'String' },
                              { value: 'enum', label: 'Enum' },
                            ]}
                            onChange={(value) =>
                              value &&
                              updateOutcomeField(stageIndex, fieldIndex, (current) => ({
                                ...current,
                                value_type: value as BrowserStageOutcomeField['value_type'],
                                enum_values: value === 'enum' ? current.enum_values ?? ['ready', 'blocked'] : [],
                                minimum: value === 'integer' ? current.minimum : undefined,
                                maximum: value === 'integer' ? current.maximum : undefined,
                              }))
                            }
                            disabled={fieldValueConfig.is_disabled || readinessReferenced}
                          />
                          <Checkbox
                            label="Required"
                            checked={outcomeField.required !== false}
                            onChange={(event) =>
                              updateOutcomeField(stageIndex, fieldIndex, (current) => ({
                                ...current,
                                required: event.currentTarget.checked,
                              }))
                            }
                            disabled={fieldValueConfig.is_disabled || readinessReferenced}
                          />
                          <ActionIcon
                            aria-label={`Remove outcome field ${fieldIndex + 1}`}
                            color="red"
                            variant="subtle"
                            onClick={() => removeOutcomeField(stageIndex, fieldIndex)}
                            disabled={fieldValueConfig.is_disabled || readinessReferenced}
                          >
                            <HiOutlineTrash size={16} />
                          </ActionIcon>
                        </Group>
                        {outcomeField.value_type === 'enum' && (
                          <StyledTextInput
                            label="Allowed values"
                            description="Comma-separated values"
                            value={(outcomeField.enum_values ?? []).join(', ')}
                            onChange={(event) =>
                              updateOutcomeField(stageIndex, fieldIndex, (current) => ({
                                ...current,
                                enum_values: event.currentTarget.value
                                  .split(',')
                                  .map((value) => value.trim())
                                  .filter(Boolean),
                              }))
                            }
                            disabled={fieldValueConfig.is_disabled || readinessReferenced}
                          />
                        )}
                        {outcomeField.value_type === 'integer' && (
                          <Group grow>
                            <StyledTextInput
                              label="Minimum"
                              value={
                                numericDrafts[numericDraftKey(stageIndex, fieldIndex, 'minimum')] ??
                                outcomeField.minimum?.toString() ??
                                ''
                              }
                              onChange={(event) =>
                                updateOutcomeIntegerBound(stageIndex, fieldIndex, 'minimum', event.currentTarget.value)
                              }
                              disabled={fieldValueConfig.is_disabled || readinessReferenced}
                              error={numericDraftError(numericDraftKey(stageIndex, fieldIndex, 'minimum'))}
                            />
                            <StyledTextInput
                              label="Maximum"
                              value={
                                numericDrafts[numericDraftKey(stageIndex, fieldIndex, 'maximum')] ??
                                outcomeField.maximum?.toString() ??
                                ''
                              }
                              onChange={(event) =>
                                updateOutcomeIntegerBound(stageIndex, fieldIndex, 'maximum', event.currentTarget.value)
                              }
                              disabled={fieldValueConfig.is_disabled || readinessReferenced}
                              error={numericDraftError(numericDraftKey(stageIndex, fieldIndex, 'maximum'))}
                            />
                          </Group>
                        )}
                      </Stack>
                    </Box>
                    );
                  })}
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => addOutcomeField(stageIndex)}
                    disabled={fieldValueConfig.is_disabled}
                  >
                    Add Outcome Field
                  </Button>
                  {stage.stage_kind === 'verification' && (
                    <Stack gap="xs" mt="xs">
                      <Divider />
                      <Group justify="space-between" align="center">
                        <div>
                          <Text size="sm" fw={500}>
                            Readiness Gate
                          </Text>
                          <Text size="xs" c="dimmed">
                            Required typed facts that must match before the flow continues.
                          </Text>
                        </div>
                        <Checkbox
                          aria-label={`Enable readiness gate for ${stage.label || stage.stage_id}`}
                          checked={!!stage.readiness_policy}
                          disabled={fieldValueConfig.is_disabled || readinessEligibleFields(stage).length === 0}
                          onChange={(event) =>
                            updateReadinessPolicy(stageIndex, (policy, currentStage) => {
                              if (!event.currentTarget.checked) {
                                return null;
                              }
                              const field = readinessEligibleFields(currentStage)[0];
                              if (!field) {
                                return null;
                              }
                              return {
                                schema_version: 'browser_stage_readiness_policy.v1',
                                conditions: [
                                  {
                                    field_name: field.name,
                                    allowed_values: defaultReadinessAllowedValues(field),
                                  },
                                ],
                              };
                            })
                          }
                        />
                      </Group>
                      {stage.readiness_policy?.conditions.map((condition, conditionIndex) => {
                        const selectedField = stage.outcome_contract.fields.find(
                          (outcomeField) => outcomeField.name === condition.field_name,
                        );
                        const selectableFields = readinessEligibleFields(stage);
                        const allowedValueOptions =
                          selectedField?.value_type === 'boolean'
                            ? [
                                { value: 'true', label: 'True' },
                                { value: 'false', label: 'False' },
                              ]
                            : (selectedField?.enum_values ?? []).map((value) => ({ value, label: value }));
                        return (
                          <Box key={`${stageIndex}-readiness-${conditionIndex}`} p="xs" bg="blue.0" style={{ borderRadius: '6px' }}>
                            <Stack gap="xs">
                              <Group grow align="end">
                                <Select
                                  label="Required field"
                                  value={condition.field_name}
                                  data={selectableFields.map((outcomeField) => ({
                                    value: outcomeField.name,
                                    label: outcomeField.name,
                                  }))}
                                  onChange={(fieldName) => {
                                    const field = selectableFields.find((outcomeField) => outcomeField.name === fieldName);
                                    if (!field) {
                                      return;
                                    }
                                    updateReadinessPolicy(stageIndex, (policy) => ({
                                      schema_version: 'browser_stage_readiness_policy.v1',
                                      conditions: (policy?.conditions ?? []).map((candidate, index) =>
                                        index === conditionIndex
                                          ? {
                                              field_name: field.name,
                                              allowed_values: defaultReadinessAllowedValues(field),
                                            }
                                          : candidate,
                                      ),
                                    }));
                                  }}
                                  disabled={fieldValueConfig.is_disabled}
                                />
                                <ActionIcon
                                  aria-label={`Remove readiness condition ${conditionIndex + 1}`}
                                  color="red"
                                  variant="subtle"
                                  onClick={() =>
                                    updateReadinessPolicy(stageIndex, (policy) => {
                                      const conditions = (policy?.conditions ?? []).filter(
                                        (_, index) => index !== conditionIndex,
                                      );
                                      return conditions.length > 0
                                        ? { schema_version: 'browser_stage_readiness_policy.v1', conditions }
                                        : null;
                                    })
                                  }
                                  disabled={fieldValueConfig.is_disabled}
                                >
                                  <HiOutlineTrash size={16} />
                                </ActionIcon>
                              </Group>
                              {selectedField?.value_type === 'integer' ? (
                                <StyledTextInput
                                  label="Allowed values"
                                  description="Comma-separated integers"
                                  value={
                                    numericDrafts[readinessDraftKey(stageIndex, conditionIndex)] ??
                                    condition.allowed_values.join(', ')
                                  }
                                  onChange={(event) => {
                                    const key = readinessDraftKey(stageIndex, conditionIndex);
                                    const nextValue = event.currentTarget.value;
                                    setNumericDrafts((drafts) => ({ ...drafts, [key]: nextValue }));
                                    const values = parseIntegerList(nextValue);
                                    if (values === null) {
                                      return;
                                    }
                                    updateReadinessPolicy(stageIndex, (policy) => ({
                                      schema_version: 'browser_stage_readiness_policy.v1',
                                      conditions: (policy?.conditions ?? []).map((candidate, index) =>
                                        index === conditionIndex ? { ...candidate, allowed_values: values } : candidate,
                                      ),
                                    }));
                                  }}
                                  disabled={fieldValueConfig.is_disabled}
                                  error={
                                    numericDrafts[readinessDraftKey(stageIndex, conditionIndex)] !== undefined &&
                                    parseIntegerList(numericDrafts[readinessDraftKey(stageIndex, conditionIndex)]) === null
                                      ? 'Enter one or more comma-separated whole numbers.'
                                      : undefined
                                  }
                                />
                              ) : (
                                <MultiSelect
                                  label="Allowed values"
                                  value={condition.allowed_values.map(String)}
                                  data={allowedValueOptions}
                                  onChange={(values) =>
                                    updateReadinessPolicy(stageIndex, (policy) => ({
                                      schema_version: 'browser_stage_readiness_policy.v1',
                                      conditions: (policy?.conditions ?? []).map((candidate, index) =>
                                        index === conditionIndex
                                          ? {
                                              ...candidate,
                                              allowed_values:
                                                selectedField?.value_type === 'boolean'
                                                  ? values.map((value) => value === 'true')
                                                  : values,
                                            }
                                          : candidate,
                                      ),
                                    }))
                                  }
                                  disabled={fieldValueConfig.is_disabled}
                                />
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                      {stage.readiness_policy && (
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() =>
                            updateReadinessPolicy(stageIndex, (policy, currentStage) => {
                              const field = readinessEligibleFields(currentStage).find(
                                (candidate) => !policy?.conditions.some((condition) => condition.field_name === candidate.name),
                              );
                              if (!field) {
                                return policy;
                              }
                              return {
                                schema_version: 'browser_stage_readiness_policy.v1',
                                conditions: [
                                  ...(policy?.conditions ?? []),
                                  {
                                    field_name: field.name,
                                    allowed_values: defaultReadinessAllowedValues(field),
                                  },
                                ],
                              };
                            })
                          }
                          disabled={
                            fieldValueConfig.is_disabled ||
                            readinessEligibleFields(stage).every((outcomeField) =>
                              stage.readiness_policy?.conditions.some(
                                (condition) => condition.field_name === outcomeField.name,
                              ),
                            )
                          }
                        >
                          Add Readiness Condition
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
              {observer && (
                <Alert color="gray" title="Post-action observation">
                  <Text size="sm">Browser Use only. These platform-owned facts are always required:</Text>
                  <Text size="sm">{platformObserverFields.join(', ')}</Text>
                  <Stack gap="xs" mt="sm">
                    <Text size="sm" fw={500}>
                      Supplemental Observer Fields
                    </Text>
                    <Text size="xs" c="dimmed">
                      Add only flow-specific facts here. Platform-owned fields above are read-only and cannot be changed.
                    </Text>
                    {observer.outcome_contract.fields.map((outcomeField, fieldIndex) => (
                      <Box key={`observer-${stageIndex}-${fieldIndex}`} p="xs" bg="gray.0" style={{ borderRadius: '6px' }}>
                        <Stack gap="xs">
                          <Group grow align="end">
                            <StyledTextInput
                              label="Name"
                              value={outcomeField.name}
                              onChange={(event) =>
                                updateOutcomeField(stageIndex + 1, fieldIndex, (current) => ({
                                  ...current,
                                  name: event.currentTarget.value,
                                }))
                              }
                              disabled={fieldValueConfig.is_disabled}
                            />
                            <Select
                              label="Type"
                              value={outcomeField.value_type}
                              data={[
                                { value: 'boolean', label: 'Boolean' },
                                { value: 'integer', label: 'Integer' },
                                { value: 'string', label: 'String' },
                                { value: 'enum', label: 'Enum' },
                              ]}
                              onChange={(value) =>
                                value &&
                                updateOutcomeField(stageIndex + 1, fieldIndex, (current) => ({
                                  ...current,
                                  value_type: value as BrowserStageOutcomeField['value_type'],
                                  enum_values: value === 'enum' ? current.enum_values ?? ['ready', 'blocked'] : [],
                                  minimum: value === 'integer' ? current.minimum : undefined,
                                  maximum: value === 'integer' ? current.maximum : undefined,
                                }))
                              }
                              disabled={fieldValueConfig.is_disabled}
                            />
                            <Checkbox
                              label="Required"
                              checked={outcomeField.required !== false}
                              onChange={(event) =>
                                updateOutcomeField(stageIndex + 1, fieldIndex, (current) => ({
                                  ...current,
                                  required: event.currentTarget.checked,
                                }))
                              }
                              disabled={fieldValueConfig.is_disabled}
                            />
                            <ActionIcon
                              aria-label={`Remove supplemental observer field ${fieldIndex + 1}`}
                              color="red"
                              variant="subtle"
                              onClick={() => removeOutcomeField(stageIndex + 1, fieldIndex)}
                              disabled={fieldValueConfig.is_disabled}
                            >
                              <HiOutlineTrash size={16} />
                            </ActionIcon>
                          </Group>
                          {outcomeField.value_type === 'enum' && (
                            <StyledTextInput
                              label="Allowed values"
                              description="Comma-separated values"
                              value={(outcomeField.enum_values ?? []).join(', ')}
                              onChange={(event) =>
                                updateOutcomeField(stageIndex + 1, fieldIndex, (current) => ({
                                  ...current,
                                  enum_values: event.currentTarget.value
                                    .split(',')
                                    .map((value) => value.trim())
                                    .filter(Boolean),
                                }))
                              }
                              disabled={fieldValueConfig.is_disabled}
                            />
                          )}
                          {outcomeField.value_type === 'integer' && (
                            <Group grow>
                              <StyledTextInput
                                label="Minimum"
                                value={
                                  numericDrafts[numericDraftKey(stageIndex + 1, fieldIndex, 'minimum')] ??
                                  outcomeField.minimum?.toString() ??
                                  ''
                                }
                                onChange={(event) =>
                                  updateOutcomeIntegerBound(
                                    stageIndex + 1,
                                    fieldIndex,
                                    'minimum',
                                    event.currentTarget.value,
                                  )
                                }
                                disabled={fieldValueConfig.is_disabled}
                                error={numericDraftError(numericDraftKey(stageIndex + 1, fieldIndex, 'minimum'))}
                              />
                              <StyledTextInput
                                label="Maximum"
                                value={
                                  numericDrafts[numericDraftKey(stageIndex + 1, fieldIndex, 'maximum')] ??
                                  outcomeField.maximum?.toString() ??
                                  ''
                                }
                                onChange={(event) =>
                                  updateOutcomeIntegerBound(
                                    stageIndex + 1,
                                    fieldIndex,
                                    'maximum',
                                    event.currentTarget.value,
                                  )
                                }
                                disabled={fieldValueConfig.is_disabled}
                                error={numericDraftError(numericDraftKey(stageIndex + 1, fieldIndex, 'maximum'))}
                              />
                            </Group>
                          )}
                        </Stack>
                      </Box>
                    ))}
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => addOutcomeField(stageIndex + 1)}
                      disabled={fieldValueConfig.is_disabled}
                    >
                      Add Supplemental Observer Field
                    </Button>
                  </Stack>
                </Alert>
              )}
              <StyledTextArea
                label="Completion Criteria"
                value={stage.completion_criteria ?? ''}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    completion_criteria: event.currentTarget.value,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
                minRows={2}
                autosize
              />
              <StyledTextArea
                label="Expected Outcome"
                value={stage.expected_outcome ?? ''}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    expected_outcome: event.currentTarget.value,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
                minRows={2}
                autosize
              />
              <Checkbox
                label="This stage may require user intervention"
                checked={!!stage.may_require_user_intervention}
                onChange={(event) =>
                  updateStage(stageIndex, (currentStage) => ({
                    ...currentStage,
                    may_require_user_intervention: event.currentTarget.checked,
                  }))
                }
                disabled={fieldValueConfig.is_disabled}
              />
              {stage.metadata && Object.keys(stage.metadata).length > 0 && (
                <>
                  <Divider />
                  <Text size="xs" c="dimmed">
                    Advanced metadata is preserved for this stage and will remain in the saved JSON.
                  </Text>
                </>
              )}
            </Stack>
          </Box>
        );
        })}

        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            The editor writes the backend-owned Browser Flow V2 contract automatically.
          </Text>
          <Group gap="xs">
          <StyledButton
            leftSection={<HiOutlinePlus size={16} />}
            variant="outline"
            onClick={() => addStage('narrative')}
            disabled={fieldValueConfig.is_disabled}
          >
            Add Narrative
          </StyledButton>
          <StyledButton variant="outline" onClick={() => addStage('verification')} disabled={fieldValueConfig.is_disabled}>
            Add Verification
          </StyledButton>
          <StyledButton
            variant="outline"
            onClick={() => addStage('deterministic_interaction')}
            disabled={fieldValueConfig.is_disabled}
          >
            Add Deterministic Interaction
          </StyledButton>
          {authoringSpec.stage_kinds.structured_extraction && !definition.stages.some((stage) => stage.stage_kind === 'structured_extraction' || stage.stage_kind === 'action') && (
            <StyledButton variant="outline" onClick={() => addStage('structured_extraction')} disabled={fieldValueConfig.is_disabled}>
              Add Structured Extraction
            </StyledButton>
          )}
          <StyledButton variant="outline" onClick={() => addStage('action')} disabled={fieldValueConfig.is_disabled || definition.stages.some((stage) => stage.stage_kind === 'structured_extraction')}>
            Add Action + Observer
          </StyledButton>
          </Group>
        </Group>
      </Stack>
    </Box>
  );
};

export default BrowserMultiStageFlowField;
